import { useState, useEffect } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../../urls';
import { AppDataGrid } from '@/components/ui';
import { GridAlignment } from '@mui/x-data-grid';
import { Loader } from '@/components/loader/loader';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { Box, Button, Link, Typography } from '@mui/material';
import { T } from '@/theme/tokens';
import ResourceDetailsModal from '@/components/resource-details/resource-details-modal';
import { getUser } from '@/lib/common';
import { DisputeDialog } from '@/components/dispute-dialog/dispute-dialog';
import DisputesModal from '@/components/disputes-table/disputes-modal';

const columns = (onResourceClick: (resourceId: number) => void) => [
  {
    field: 'resource_name',
    flex: 1,
    headerName: 'Resource Name',
    renderCell: (params: any) => (
      <Link
        component="button"
        variant="body2"
        onClick={() => onResourceClick(params.row.id)}
        sx={{
          textAlign: 'left',
          color: T.primary,
          fontWeight: 700,
          textDecoration: 'none',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline' },
          background: 'none',
          border: 'none',
          padding: 0,
        }}>
        {params.value}
      </Link>
    ),
  },
  {
    field: 'resource_type',
    flex: 1,
    headerName: 'Resource Type',
  },
  {
    field: 'provider',
    flex: 0.25,
    headerName: 'Cloud',
  },
  {
    field: 'resource_owner',
    flex: 0.5,
    headerName: 'Resource Owner',
  },
  {
    field: 'department_name',
    flex: 0.5,
    headerName: 'Department Name',
  },
  {
    field: 'system_name',
    flex: 0.5,
    headerName: 'System Name',
  },
  {
    field: 'current_cost_original_currency',
    flex: 0.25,
    headerName: 'Cost',
    align: 'right' as GridAlignment,
    headerAlign: 'right' as GridAlignment,
    valueFormatter: (params: any) => {
      if (params.value === null || params.value === undefined) {
        return 'n/a';
      }
      return formatCurrencyNoUnit(params.value);
    },
  },
  {
    field: 'original_currency',
    flex: 0.1,
    headerName: 'Currency',
  },
];

interface ResourcesCostsProps {
  filters?: string;
}

export function ResourcesCosts({ filters }: Readonly<ResourcesCostsProps>) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<number | null>(null);
  const [resourceModalOpen, setResourceModalOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<number[]>([]);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [dispute, setDispute] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);

  const user = getUser();
  const currency = user?.default_currency || 'USD';

  useEffect(() => {
    setLoading(true);

    fetchWrapper
      .get(urls.DevOps.getResourcesCosts + (filters ? `?${filters}` : ''))
      .then((data) => {
        setItems(data);
      })
      .catch((error) => console.error('There was an error!', error))
      .finally(() => {
        setLoading(false);
      });
  }, [filters]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        // Dispute dialog only needs id/firstName/lastName/username. Prefer light view to
        // avoid coupling Reporting to full user management payload (and legacy budget joins).
        const usersData = await fetchWrapper.get(urls.DevOps.getUsersFilter);
        setAllUsers(usersData || []);
      } catch (error) {
        console.error('Error fetching users for dispute:', error);
      }
    };
    fetchAllUsers();
  }, []);

  const handleResourceClick = (resourceId: number) => {
    setSelectedResourceId(resourceId);
    setResourceModalOpen(true);
  };

  const selectedRows = items.filter((row) => rowSelectionModel.includes(row.id));

  const handleCreateDisputeSubmit = async (title: string, receiverId: number) => {
    if (rowSelectionModel.length === 0) return;
    try {
      const createResponse = await fetchWrapper.post(urls.DevOps.createDisputeV2, {
        id_receiver: receiverId,
        sender: user?.username,
        title,
        type: 'Resource',
        resource_ids: rowSelectionModel,
        action_center_id: null,
      });

      const disputeId =
        createResponse?.[0]?.[0]?.dispute_id ??
        (await fetchWrapper.get(urls.DevOps.getMaxDispute))?.[0]?.disputeid;

      if (disputeId) {
        const response = await fetchWrapper.get(
          urls.DevOps.getDispute.replace(':id', String(disputeId)),
        );
        setDispute([response.dispute]);
        setDisputeDialogOpen(false);
        setRowSelectionModel([]);
        setDisputeModalOpen(true);
      }
    } catch (error) {
      console.error('Error creating dispute:', error);
    }
  };

  const rows = items;

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {rowSelectionModel.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            {rowSelectionModel.length} resource{rowSelectionModel.length !== 1 ? 's' : ''} selected
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            Selected: {selectedRows.map((r) => r.resource_name || r.id).join(', ')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setDisputeDialogOpen(true)}>
              Create dispute
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setRowSelectionModel([])}>
              Clear selection
            </Button>
          </Box>
        </Box>
      )}
      <AppDataGrid
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        rows={rows}
        columns={columns(handleResourceClick)}
        autoHeight={false}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newSelection) => setRowSelectionModel(newSelection as number[])}
        getRowId={(row: any) => row.id}
      />
      <ResourceDetailsModal
        open={resourceModalOpen}
        onClose={() => {
          setResourceModalOpen(false);
          setSelectedResourceId(null);
        }}
        resourceId={selectedResourceId}
        currency={currency}
      />
      <DisputeDialog
        open={disputeDialogOpen}
        onClose={() => setDisputeDialogOpen(false)}
        onSubmit={handleCreateDisputeSubmit}
        defaultReceiverId={null}
        currentUserId={user?.id ?? 0}
        showUserSelector={true}
        availableUsers={allUsers}
      />
      {disputeModalOpen && dispute[0] && (
        <DisputesModal
          Dispute={{
            ...(dispute[0] as any),
            id_dispute: (dispute[0] as any).id,
          }}
          IsModalOpened={disputeModalOpen}
          onCloseModal={() => {
            setDisputeModalOpen(false);
            setDispute([]);
          }}
          zIndex={1400}
        />
      )}
    </>
  );
}
